import React from 'react';
import { Trash2, AlertCircle, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';

const CartItemRow = ({ item, isUnavailable, deliveryDate, getDayName, formatAvailableDays, onRemove }) => (
  <div className={`flex items-start gap-3 p-2 rounded-lg ${isUnavailable ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
    <img src={item.image_url} alt={item.name} className="w-12 h-12 object-cover rounded" />
    <div className="flex-1 min-w-0">
      <p className="font-medium text-sm truncate">{item.name}</p>
      {item.has_discount ? (
        <p className="text-xs text-gray-500">
          {item.quantity} × <span className="line-through text-gray-400">${item.wholesale_price.toFixed(2)}</span>{' '}
          <span className="text-green-600 font-medium">${item.your_price.toFixed(2)}</span>
        </p>
      ) : (
        <p className="text-xs text-gray-500">
          {item.quantity} × ${item.wholesale_price.toFixed(2)}
        </p>
      )}
      {isUnavailable && (
        <div className="flex items-center gap-1 mt-1">
          <AlertTriangle className="h-3 w-3 text-red-500" />
          <span className="text-xs text-red-600">Not available on {getDayName(deliveryDate)}s</span>
        </div>
      )}
      {item.available_days && item.available_days.length > 0 && (
        <Badge className="text-xs bg-amber-100 text-amber-700 mt-1">
          {formatAvailableDays(item.available_days)} only
        </Badge>
      )}
    </div>
    <div className="flex items-center gap-2">
      <div className="text-right">
        <p className="text-sm font-semibold">
          ${((item.your_price || item.wholesale_price) * item.quantity).toFixed(2)}
        </p>
        {item.has_discount && (
          <p className="text-xs text-green-600">-{(item.discount_amount * item.quantity).toFixed(2)}</p>
        )}
      </div>
      <button onClick={() => onRemove(item.id)} className="text-gray-400 hover:text-red-500">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  </div>
);

export const OrderSummaryCard = ({
  cart,
  deliveryDate,
  isProductAvailableForDate,
  getDayName,
  formatAvailableDays,
  getCartSavings,
  cartTotal,
  minimumOrder,
  unavailableItems,
  hasUnavailableItems,
  isValidOrder,
  selectedLocation,
  submitting,
  onRemoveItem,
  onSubmit,
  canPlaceOrders = true,
}) => (
  <Card className="sticky top-24" data-testid="order-summary-card">
    <CardHeader>
      <CardTitle className="text-lg">Order Summary</CardTitle>
    </CardHeader>
    <CardContent>
      {/* Cart Items */}
      <div className="space-y-3 max-h-64 overflow-y-auto">
        {cart.map((item) => (
          <CartItemRow
            key={item.id}
            item={item}
            isUnavailable={deliveryDate && !isProductAvailableForDate(item, deliveryDate)}
            deliveryDate={deliveryDate}
            getDayName={getDayName}
            formatAvailableDays={formatAvailableDays}
            onRemove={onRemoveItem}
          />
        ))}
      </div>

      {/* Totals */}
      <div className="border-t mt-4 pt-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Subtotal</span>
          <span>${cartTotal.toFixed(2)}</span>
        </div>
        {getCartSavings() > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Your Savings</span>
            <span>-${getCartSavings().toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Delivery</span>
          <span className="text-green-600">Free</span>
        </div>
        <div className="flex justify-between text-lg font-bold pt-2 border-t">
          <span>Total</span>
          <span className="text-amber-600">${cartTotal.toFixed(2)}</span>
        </div>
      </div>

      {/* Unavailable Items Warning */}
      {hasUnavailableItems && (
        <div className="mt-4 p-3 bg-red-50 rounded-lg flex items-start gap-2" data-testid="unavailable-items-warning">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Items not available for {deliveryDate ? format(deliveryDate, 'EEEE') : 'selected date'}
            </p>
            <p className="text-xs text-red-600 mt-1">
              {unavailableItems.length} item(s) in your cart cannot be delivered on {deliveryDate ? getDayName(deliveryDate) : 'the selected day'}s.
              Please remove them or choose a different delivery date.
            </p>
            <ul className="mt-2 text-xs text-red-700">
              {unavailableItems.map((item) => (
                <li key={item.id} className="flex items-center justify-between py-1">
                  <span>• {item.name}</span>
                  <span className="text-red-500">({formatAvailableDays(item.available_days)} only)</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Minimum Order Warning */}
      {cartTotal < minimumOrder && (
        <div className="mt-4 p-3 bg-red-50 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Minimum order not met</p>
            <p className="text-xs text-red-600">
              Add ${(minimumOrder - cartTotal).toFixed(2)} more to meet the ${minimumOrder.toFixed(2)} minimum.
            </p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      {!canPlaceOrders && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="no-order-permission-notice">
          <p className="text-sm text-amber-800">
            You don't have permission to place orders for this account. Please contact your account owner.
          </p>
        </div>
      )}
      <Button
        className="w-full mt-6 bg-amber-500 hover:bg-amber-600"
        size="lg"
        onClick={onSubmit}
        disabled={submitting || !isValidOrder || !selectedLocation || !deliveryDate || !canPlaceOrders}
        data-testid="submit-order-btn"
      >
        {submitting ? 'Submitting...' : 'Submit Order'}
      </Button>

      <p className="text-xs text-gray-500 text-center mt-2">
        By submitting, you agree to our terms and conditions
      </p>
    </CardContent>
  </Card>
);
